import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiError, imgSrc, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const CATEGORIES = ["Resin Art", "Macramé", "Ceramics & Pottery", "Candles & Fragrance"];

const EMPTY = {
  name: "",
  category: CATEGORIES[0],
  price: "",
  compare_at_price: "",
  short_desc: "",
  description: "",
  imagesText: "",
  stock: "",
  tags: "",
  featured: false,
  active: true,
};

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/products")
      .then((r) => setProducts(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      price: p.price,
      compare_at_price: p.compare_at_price || "",
      short_desc: p.short_desc || "",
      description: p.description || "",
      imagesText: (p.images || []).join("\n"),
      stock: p.stock,
      tags: (p.tags || []).join(", "),
      featured: p.featured,
      active: p.active,
    });
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, imagesText: (f.imagesText ? f.imagesText + "\n" : "") + data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    e.target.value = "";
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      price: Number(form.price),
      compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
      short_desc: form.short_desc,
      description: form.description,
      images: form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      stock: Number(form.stock || 0),
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      featured: form.featured,
      active: form.active,
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl">Products</h2>
        <button onClick={openNew} className="btn-solid !px-5 !py-2.5 text-xs" data-testid="admin-new-product-btn">
          <Plus size={15} /> New product
        </button>
      </div>
      <div className="overflow-x-auto border border-line bg-white" data-testid="admin-products-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Live</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-row-${p.slug}`}>
                <TableCell>
                  <img src={imgSrc(p.images?.[0])} alt="" className="h-10 w-10 border border-line object-cover" />
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-stone2">{p.category}</TableCell>
                <TableCell>{inr(p.price)}</TableCell>
                <TableCell>{p.stock}</TableCell>
                <TableCell>{p.featured ? "Yes" : "—"}</TableCell>
                <TableCell>{p.active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => openEdit(p)} className="pr-3 text-xs font-semibold uppercase tracking-wide text-stone2 hover:text-terracotta" data-testid={`admin-edit-product-${p.slug}`}>
                    Edit
                  </button>
                  <button onClick={() => remove(p)} className="text-xs font-semibold uppercase tracking-wide text-terracotta hover:text-red-700" data-testid={`admin-delete-product-${p.slug}`}>
                    Delete
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-line bg-parchment" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label-mono mb-1.5 block">Name</label>
              <input className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-product-name" />
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Category</label>
              <select className="input-base" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-product-category">
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Stock</label>
              <input type="number" className="input-base" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="admin-product-stock" />
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Price (₹)</label>
              <input type="number" className="input-base" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-product-price" />
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Compare-at price (optional)</label>
              <input type="number" className="input-base" value={form.compare_at_price} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })} data-testid="admin-product-compare-price" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-mono mb-1.5 block">Short description</label>
              <input className="input-base" value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} data-testid="admin-product-short-desc" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-mono mb-1.5 block">Full description</label>
              <textarea rows={4} className="input-base resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-product-description" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-mono mb-1.5 block">Image URLs (one per line)</label>
              <textarea rows={3} className="input-base resize-none" value={form.imagesText} onChange={(e) => setForm({ ...form, imagesText: e.target.value })} placeholder="https://…" data-testid="admin-product-images" />
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 border border-stone-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-colors hover:border-ink" data-testid="admin-upload-image-btn">
                Upload image
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="admin-upload-image-input" />
              </label>
            </div>
            <div>
              <label className="label-mono mb-1.5 block">Tags (comma separated)</label>
              <input className="input-base" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="admin-product-tags" />
            </div>
            <div className="flex items-end gap-8 pb-1">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="admin-product-featured" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="admin-product-active" /> Live
              </label>
            </div>
          </div>
          <button onClick={save} disabled={saving} className="btn-solid mt-2 w-full" data-testid="admin-save-product-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add product"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
